import { createSlice } from "@reduxjs/toolkit";

const teamSlice = createSlice({
  name: "team",
  initialState: {
    teamMembers: [],
    availableRoles: [],
    
    //Todo => Loading states
    isLoading: false,
    isCreating: false,
    isUpdating: false,
    isDeleting: false,
    
    //Todo => Error states
    error: null,
    createError: null,
    updateError: null,
    deleteError: null,
    
    //Todo => UI states
    totalMembers: 0,
    activeMembers: 0,
    inactiveMembers: 0,
    
    //Todo => Validation states
    canCreateMembers: false,
  },
  reducers: {
    //Todo => Loading states
    setLoading: (state, action) => {
      state.isLoading = action.payload;
      if (action.payload) {
        state.error = null;
      }
    },
    setCreating: (state, action) => {
      state.isCreating = action.payload;
      if (action.payload) {
        state.createError = null;
      }
    },
    setUpdating: (state, action) => {
      state.isUpdating = action.payload;
      if (action.payload) {
        state.updateError = null;
      }
    },
    setDeleting: (state, action) => {
      state.isDeleting = action.payload;
      if (action.payload) {
        state.deleteError = null;
      }
    },

    //Todo => Error state reducers
    setError: (state, action) => {
      state.error = action.payload;
      state.isLoading = false;
    },
    setCreateError: (state, action) => {
      state.createError = action.payload;
      state.isCreating = false;
    },
    setUpdateError: (state, action) => {
      state.updateError = action.payload;
      state.isUpdating = false;
    },
    setDeleteError: (state, action) => {
      state.deleteError = action.payload;
      state.isDeleting = false;
    },

    //Todo => Team members management reducers
    setTeamMembers: (state, action) => {
      state.teamMembers = action.payload;
      state.isLoading = false;
      state.error = null;
      
      //Todo => Update statistics - handle both API format (active/inactive) and UI format (Active/Inactive)
      state.totalMembers = action.payload.length;
      state.activeMembers = action.payload.filter(member => 
        member.status === 'active' || member.status === 'Active'
      ).length;
      state.inactiveMembers = action.payload.filter(member => 
        member.status === 'inactive' || member.status === 'Inactive'
      ).length;
    },

    addTeamMember: (state, action) => {
      state.teamMembers.push(action.payload);
      state.isCreating = false;
      state.createError = null;
      
      //Todo => Update statistics
      state.totalMembers = state.teamMembers.length;
      if (action.payload.status === 'active' || action.payload.status === 'Active') {
        state.activeMembers += 1;
      } else {
        state.inactiveMembers += 1;
      }
    },

    updateTeamMember: (state, action) => {
      const { id, ...updatedData } = action.payload;
      const index = state.teamMembers.findIndex(member => member.id === id);
      
      if (index !== -1) {
        const oldStatus = state.teamMembers[index].status;
        state.teamMembers[index] = { ...state.teamMembers[index], ...updatedData };
        
        //Todo => Update statistics if status changed
        const newStatus = updatedData.status;
        if (oldStatus !== newStatus) {
          const wasActive = oldStatus === 'active' || oldStatus === 'Active';
          const isActive = newStatus === 'active' || newStatus === 'Active';
          
          if (wasActive && !isActive) {
            state.activeMembers -= 1;
            state.inactiveMembers += 1;
          } else if (!wasActive && isActive) {
            state.activeMembers += 1;
            state.inactiveMembers -= 1;
          }
        }
      }
      
      state.isUpdating = false;
      state.updateError = null;
    },

    removeTeamMember: (state, action) => {
      const memberId = action.payload;
      const memberIndex = state.teamMembers.findIndex(member => member.id === memberId);
      
      if (memberIndex !== -1) {
        const removedMember = state.teamMembers[memberIndex];
        state.teamMembers.splice(memberIndex, 1);
        
        //Todo => Update statistics
        state.totalMembers = state.teamMembers.length;
        if (removedMember.status === 'active' || removedMember.status === 'Active') {
          state.activeMembers -= 1;
        } else {
          state.inactiveMembers -= 1;
        }
      }
      
      state.isDeleting = false;
      state.deleteError = null;
    },

    updateTeamMemberStatus: (state, action) => {
      const { id, status } = action.payload;
      const member = state.teamMembers.find(member => member.id === id);
      
      if (member) {
        const oldStatus = member.status;
        member.status = status;
        
        //Todo => Update statistics
        if (oldStatus !== status) {
          const wasActive = oldStatus === 'active' || oldStatus === 'Active';
          const isActive = status === 'active' || status === 'Active';
          
          if (wasActive && !isActive) {
            state.activeMembers -= 1;
            state.inactiveMembers += 1;
          } else if (!wasActive && isActive) {
            state.activeMembers += 1;
            state.inactiveMembers -= 1;
          }
        }
      }
      
      state.isUpdating = false;
      state.updateError = null;
    },

    //Todo => Available roles management
    setAvailableRoles: (state, action) => {
      state.availableRoles = action.payload;
    },

    //Todo => Validation states
    setCanCreateMembers: (state, action) => {
      state.canCreateMembers = action.payload;
    },

    //Todo => Clear all errors
    clearErrors: (state) => {
      state.error = null;
      state.createError = null;
      state.updateError = null;
      state.deleteError = null;
    },

    //Todo => Reset team state (for logout)
    reset: (state) => {
      state.teamMembers = [];
      state.availableRoles = [];
      state.isLoading = false;
      state.isCreating = false;
      state.isUpdating = false;
      state.isDeleting = false;
      state.error = null;
      state.createError = null;
      state.updateError = null;
      state.deleteError = null;
      state.totalMembers = 0;
      state.activeMembers = 0;
      state.inactiveMembers = 0;
      state.canCreateMembers = false;
    }
  }
});

export const {
  //Todo => Loading states
  setLoading,
  setCreating,
  setUpdating,
  setDeleting,
  
  //Todo => Error states
  setError,
  setCreateError,
  setUpdateError,
  setDeleteError,
  
  //Todo => Team members management
  setTeamMembers,
  addTeamMember,
  updateTeamMember,
  removeTeamMember,
  updateTeamMemberStatus,
  
  //Todo => Available roles management
  setAvailableRoles,
  
  //Todo => Validation states
  setCanCreateMembers,
  
  //Todo => Utility actions
  clearErrors,
  reset
} = teamSlice.actions;

export default teamSlice.reducer; 